import * as React from "react";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
  GridSortModel,
  GridRowModel,
  GridRowClassNameParams,
} from "@mui/x-data-grid";
import {
  useDeleteCertificationMutation,
  useGetCertificationsQuery,
  useRestoreCertificationMutation,
} from "../../services/certification.service";
import { Stack, IconButton, useMediaQuery, useTheme } from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import NoResultsOverlay from "../../components/NoResultOverlay";
import { toast } from "react-toastify";
import ModalCertificationForm from "./ModalCertificationForm";
import AddIcon from "@mui/icons-material/Add";
import { Download as DownloadIcon } from "@mui/icons-material";
import useDebounce from "../../hooks/useDebounce";
import { useTitle } from "../../hooks/useTitle";
import { Certification } from "../../types/certification";
import { CardBase, GlassDataGrid, StyledTextField, Button, OrangeLinearProgress } from "../../components/global";
import dayjs from "dayjs";

const CertificationsPage = () => {
  useTitle("Certifications");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [deleteModal, setDeleteModal] = React.useState<{
    open: boolean;
    row: GridRowModel<Certification> | null;
  }>({
    open: false,
    row: null,
  });
  const [formModal, setFormModal] = React.useState<{
    open: boolean;
    data: Certification | null;
  }>({
    open: false,
    data: null,
  });
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined
  );
  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const debounceQuery = useDebounce(query, 500);

  React.useEffect(() => {
    setSearchQuery(debounceQuery);
  }, [debounceQuery]);

  const { data, isLoading, isFetching } = useGetCertificationsQuery({
    page,
    pageSize,
    sortField: sortModel.length > 0 ? sortModel[0].field : undefined,
    sortMode: sortModel.length > 0 ? sortModel[0].sort : undefined,
    search: searchQuery,
  });
  const rowCount = React.useMemo(() => data?.meta.total, [data]);

  const [doDelete, { isLoading: deleting }] = useDeleteCertificationMutation();
  const [doRestore, { isLoading: restoring }] =
    useRestoreCertificationMutation();

  const handleDeleteRow = React.useCallback((row: GridRowModel<Certification>) => {
    setDeleteModal({ open: true, row });
  }, []);
  const handleEditRow = React.useCallback((row: GridRowModel<Certification>) => {
    setFormModal({ open: true, data: row });
  }, []);
  const handleRestoreRow = React.useCallback(async (row: GridRowModel<Certification>) => {
    try {
      await doRestore(row.id).unwrap();
      toast.success("Data restored");
    } catch (err) { }
  }, [doRestore]);

  const rows: GridRowsProp | undefined = React.useMemo(
    () =>
      data?.data.map((it) => ({
        id: it.id,
        name: it.name,
        provider: it.provider,
        description: it.description,
        deleted_at: it.deleted_at,
      })),
    [data]
  );

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: "name",
        headerName: "Certification Name",
        minWidth: isMobile ? 200 : 400,
        sortingOrder: ["desc", "asc", null],
        filterable: true,
      },
      {
        field: "provider",
        headerName: "Provider",
        flex: 1,
        sortable: true,
        filterable: true,
      },
      {
        field: "description",
        headerName: "Description",
        hide: true,
      },
      {
        field: "control",
        headerName: "",
        sortable: false,
        filterable: false,
        hide: false,
        width: 150,
        minWidth: 150,
        maxWidth: 150,
        flex: 0,
        align: "center",
        headerAlign: "center",
        renderCell: (params: GridRenderCellParams<undefined, Certification>) => {
          if (!!params.row.deleted_at) {
            return (
              <Button
                variant="outlined"
                onClick={() => handleRestoreRow(params.row)}
                size="small"
                sx={{
                  color: "#F4A259",
                  borderColor: "#F4A259",
                  "&:hover": {
                    borderColor: "#F4A259",
                    backgroundColor: "rgba(244, 162, 89, 0.1)",
                  },
                }}
              >
                Restore
              </Button>
            );
          }
          return (
            <Stack direction="row" spacing={0.5} justifyContent="center">
              <IconButton
                size="small"
                onClick={() => handleEditRow(params.row)}
                sx={{
                  color: "#F4A259",
                  "&:hover": {
                    backgroundColor: "rgba(244, 162, 89, 0.1)",
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleDeleteRow(params.row)}
                sx={{
                  color: "#d32f2f",
                  "&:hover": {
                    backgroundColor: "rgba(211, 47, 47, 0.1)",
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          );
        },
        disableExport: true,
      },
    ],
    [handleRestoreRow, handleEditRow, handleDeleteRow, isMobile]
  );

  const handleCreate = React.useCallback(() => {
    setFormModal({ open: true, data: null });
  }, []);

  const closeDeleteModal = () =>
    setDeleteModal({ ...deleteModal, open: false });

  const handleDelete = async () => {
    if (deleteModal.row?.id) {
      try {
        await doDelete(deleteModal.row.id).unwrap();
        closeDeleteModal();
        toast.success("Successfully deleted");
      } catch (err) {
        closeDeleteModal();
      }
    }
  };

  const handleExport = React.useCallback(() => {
    if (!rows || rows.length === 0) return;

    // Get visible columns (excluding control column)
    const visibleColumns = columns.filter((col) => col.field !== "control");

    // Create CSV headers
    const headers = visibleColumns.map((col) => `"${col.headerName}"`).join(",");

    // Create CSV rows
    const csvRows = rows.map((row: any) => {
      return visibleColumns
        .map((col) => {
          let value: string = "";
          if (col.field) {
            const rawValue = (row as any)[col.field];
            value = rawValue ? String(rawValue) : "";
          }
          // Escape quotes and wrap in quotes if needed
          if (value && (value.includes(",") || value.includes('"') || value.includes("\n"))) {
            value = `"${value.replace(/"/g, '""')}"`;
          }
          return value || "";
        })
        .join(",");
    });

    // Combine header and rows
    const csvContent = [headers, ...csvRows].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `Certifications_${dayjs().format("YYYY-MM-DD")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [rows, columns]);

  const getRowClassName = (params: GridRowClassNameParams) => {
    return !!params.row.deleted_at ? "deleted" : "";
  };

  return (
    <>
      <CardBase
        title="Certifications"
        useGlassCard={false}
        sx={{ height: isMobile ? "70vh" : "85vh", p: 0 }}
        headerActions={
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={1}
            alignItems={isMobile ? "stretch" : "center"}
            sx={{ width: isMobile ? "100%" : "auto" }}
          >
            <StyledTextField
              size="small"
              label="Enter to search.."
              value={query || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
              sx={{ minWidth: isMobile ? "100%" : 250 }}
            />
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                width: isMobile ? "100%" : "auto",
                flexWrap: isMobile ? "wrap" : "nowrap",
              }}
            >
              {!!query && (
                <Button
                  variant="text"
                  onClick={() => setQuery("")}
                  size="small"
                  sx={{
                    color: "#F4A259",
                    "&:hover": {
                      backgroundColor: "rgba(244, 162, 89, 0.08)",
                    },
                  }}
                >
                  Reset
                </Button>
              )}
              <Button
                variant="contained"
                onClick={handleCreate}
                startIcon={<AddIcon />}
                size="small"
                sx={{
                  backgroundColor: "#F4A259",
                  color: "#ffffff",
                  fontWeight: 600,
                  boxShadow: "0 2px 8px rgba(244, 162, 89, 0.3)",
                  "&:hover": {
                    backgroundColor: "#E8944A",
                    boxShadow: "0 4px 12px rgba(244, 162, 89, 0.4)",
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                {isMobile ? "Add" : "Add Certification"}
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                disabled={!rows || rows.length === 0}
                size="small"
                sx={{
                  color: "#F4A259",
                  borderColor: "#F4A259",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "#F4A259",
                    backgroundColor: "rgba(244, 162, 89, 0.08)",
                    transform: "translateY(-1px)",
                  },
                  "&:disabled": {
                    borderColor: "rgba(244, 162, 89, 0.5)",
                    color: "rgba(244, 162, 89, 0.5)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                {isMobile ? "Export" : "Export CSV"}
              </Button>
            </Stack>
          </Stack>
        }
      >
        <GlassDataGrid
          rows={rows || []}
          columns={columns}
          paginationMode="server"
          rowCount={rowCount}
          page={page}
          onPageChange={(newPage: number) => setPage(newPage)}
          pageSize={pageSize}
          rowsPerPageOptions={[25, 50, 100]}
          onPageSizeChange={(newSize: number) => setPageSize(newSize)}
          sortModel={sortModel}
          sortingMode="server"
          onSortModelChange={(newSortModel: GridSortModel) => setSortModel(newSortModel)}
          loading={isLoading || isFetching}
          components={{
            LoadingOverlay: OrangeLinearProgress,
            NoResultsOverlay: NoResultsOverlay,
          }}
          disableSelectionOnClick
          disableColumnFilter
          density="compact"
          height={isMobile ? "calc(70vh - 120px)" : "calc(85vh - 80px)"}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "transparent",
              borderTop: "none",
              paddingLeft: isMobile ? theme.spacing(0.5) : theme.spacing(3),
              paddingRight: isMobile ? theme.spacing(0.5) : theme.spacing(3),
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "#F4A259",
                fontWeight: 600,
                fontSize: isMobile ? "0.75rem" : "0.875rem",
              },
              "& .MuiDataGrid-columnHeader": {
                color: "#F4A259",
                "& .MuiDataGrid-iconButtonContainer": {
                  color: "#F4A259",
                },
                "& .MuiDataGrid-sortIcon": {
                  color: "#F4A259",
                },
              },
            },
            "& .MuiDataGrid-cell": {
              paddingLeft: isMobile ? theme.spacing(0.5) : theme.spacing(3),
              paddingRight: isMobile ? theme.spacing(0.5) : theme.spacing(3),
              py: isMobile ? 1 : 2,
              fontSize: isMobile ? "0.75rem" : "0.875rem",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(244, 162, 89, 0.05)",
            },
            "& .deleted": {
              fontStyle: "italic",
              color: "#aaa",
            },
          }}
          getRowClassName={getRowClassName}
        />
      </CardBase>
      <ConfirmDeleteModal
        open={deleteModal.open}
        onClose={closeDeleteModal}
        onDelete={handleDelete}
        message={`Delete certification '${deleteModal.row?.name}' ?`}
      />
      <ModalCertificationForm
        open={formModal.open}
        onClose={() => setFormModal({ open: false, data: null })}
        data={formModal.data}
      />
    </>
  );
};
export default CertificationsPage;
